// src/eventsettings/eventpdfs.jsx
// PDF設定（管理者・主催者で共通）：冊子の表用・裏用PDFを、それぞれ複数枚アップロードして並べる。
// 冊子は「表用 → 自動生成の表 → 固定PDF → 自動生成の表 → 裏用」の順（1ページ＝PDF1枚）。
// 表用の先頭が表紙、裏用の最後が裏表紙になる。
// 自動生成の表と固定PDFは、場所だけ表示する（ここでは操作しない）。
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const API_URL = import.meta.env.VITE_API_URL;

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 1ファイルあたり（backend の pdfs.py の MAX_SIZE と同じ）
const MAX_TOTAL_SIZE = 95 * 1024 * 1024; // 1回の追加の合計（nginx の client_max_body_size 100m に、送信の余白を残した値）

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)}MB`
    : `${Math.max(1, Math.round(bytes / 1024))}KB`;

// 並び順の位置から、表示するラベルを決める
const pageLabel = (kind, index, total) => {
  if (kind === "front" && index === 0) return "表紙";
  if (kind === "back" && index === total - 1) return "裏表紙";
  return `${index + 1}枚目`;
};

/* ========================================
 * PDF一覧の枠（表用・裏用）
 * ======================================== */
function PdfList({ eventId, kind, label, items }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState(null); // { type: "ok" | "error", text }
  const baseUrl = `${API_URL}/api/events/${eventId}/pdfs/${kind}/`;

  const onSaved = (data, text) => {
    queryClient.setQueryData(["event-pdfs", eventId], data);
    setMessage(text ? { type: "ok", text } : null);
  };
  const onFailed = (err, fallback) =>
    setMessage({ type: "error", text: err?.response?.data?.message || fallback });

  const add = useMutation({
    mutationFn: async (files) => {
      const form = new FormData();
      files.forEach((f) => form.append("file", f));
      return (await axios.post(baseUrl, form)).data.data;
    },
    onSuccess: (data) => onSaved(data, "追加しました"),
    onError: (err) =>
      onFailed(
        err,
        // nginx がサイズ超過で弾いたとき（アプリのメッセージがない）
        err?.response?.status === 413
          ? "一度に追加できる上限（100MB）を超えています。数枚ずつに分けて追加してください"
          : "追加に失敗しました"
      ),
  });

  const reorder = useMutation({
    mutationFn: async (ids) => (await axios.put(baseUrl, { ids })).data.data,
    onSuccess: (data) => onSaved(data, null),
    onError: (err) => onFailed(err, "並べ替えに失敗しました"),
  });

  const remove = useMutation({
    mutationFn: async (pdfId) => (await axios.delete(`${baseUrl}${pdfId}/`)).data.data,
    onSuccess: (data) => onSaved(data, "削除しました"),
    onError: (err) => onFailed(err, "削除に失敗しました"),
  });

  const busy = add.isPending || reorder.isPending || remove.isPending;

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // 同じファイルをもう一度選べるようにする
    if (files.length === 0) return;

    const tooBig = files.find((f) => f.size > MAX_FILE_SIZE);
    if (tooBig) {
      setMessage({ type: "error", text: `${tooBig.name}：ファイルサイズは20MB以内にしてください` });
      return;
    }
    const total = files.reduce((sum, f) => sum + f.size, 0);
    if (total > MAX_TOTAL_SIZE) {
      setMessage({
        type: "error",
        text: `選んだ${files.length}枚の合計（${formatSize(total)}）が、一度に追加できる上限（100MB）を超えています。数枚ずつに分けて追加してください`,
      });
      return;
    }

    setMessage(null);
    add.mutate(files);
  };

  const move = (index, delta) => {
    const ids = items.map((it) => it.event_pdf_id);
    const target = index + delta;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setMessage(null);
    reorder.mutate(ids);
  };

  // ログイン情報（Authorization）が必要なので、取得してから新しいタブで開く
  const handlePreview = async (pdfId) => {
    setMessage(null);
    try {
      const res = await axios.get(`${baseUrl}${pdfId}/`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      onFailed(err, "PDFを開けませんでした");
    }
  };

  const handleRemove = (item, index) => {
    const name = pageLabel(kind, index, items.length);
    if (window.confirm(`${label}の「${name}」（${item.file_name}）を削除しますか？`)) {
      setMessage(null);
      remove.mutate(item.event_pdf_id);
    }
  };

  return (
    <div className="evSetCard">
      <div className="evSetCardHead">
        <span>{label}</span>
      </div>

      {items.length === 0 ? (
        <div className="evSetPdfMeta">未登録</div>
      ) : (
        <ol className="evSetPdfList">
          {items.map((item, index) => (
            <li key={item.event_pdf_id} className="evSetPdfItem">
              <span className="evSetPdfBadge">{pageLabel(kind, index, items.length)}</span>
              <span className="evSetPdfFile">
                <span className="evSetPdfName">{item.file_name}</span>
                <span className="evSetPdfMeta">{formatSize(item.file_size)}</span>
              </span>
              <span className="evSetPdfBtns">
                <button
                  type="button"
                  className="evSetBtn isSmall"
                  aria-label="上へ"
                  disabled={busy || index === 0}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="evSetBtn isSmall"
                  aria-label="下へ"
                  disabled={busy || index === items.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="evSetBtn isSmall"
                  onClick={() => handlePreview(item.event_pdf_id)}
                >
                  確認
                </button>
                <button
                  type="button"
                  className="evSetBtn isDanger"
                  disabled={busy}
                  onClick={() => handleRemove(item, index)}
                >
                  削除
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}

      <div className="evSetActions">
        <label className={`evSetBtn isPrimary ${busy ? "isDisabled" : ""}`}>
          {add.isPending ? "アップロード中..." : "＋ PDFを追加"}
          <input
            type="file"
            accept="application/pdf"
            multiple
            hidden
            disabled={busy}
            onChange={handleFiles}
          />
        </label>
        {message && (
          <span className={`evSetMsg ${message.type === "ok" ? "isOk" : "isError"}`}>
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
}

/* ========================================
 * コンポーネント
 * ======================================== */
export default function EventPdfSettings({ eventId, basePath, dirty }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["event-pdfs", eventId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/events/${eventId}/pdfs/`);
      if (!res.data?.success) throw new Error("PDF設定の取得に失敗しました");
      return res.data.data;
    },
    staleTime: 0,
    refetchOnMount: "always",
  });

  if (isLoading) return <div className="evSetLoading">読み込み中...</div>;
  if (isError) return <div className="evSetMsg isError">PDF設定の取得に失敗しました</div>;

  return (
    <>
      <div className="evSetPdfIntro">
        <p className="evSetSub">
          冊子は、上から下の順に並びます。（1ページ＝PDF1枚）
          <br />
          表用の1枚目が表紙、裏用の最後の1枚が裏表紙になります。
          <br />
          追加・削除・並べ替えは、すぐに反映されます。
          <br />
          一度に追加できるのは、合計100MBまでです（1ファイルは20MBまで）。多いときは、分けて追加してください。
        </p>
        <Link
          to={`${basePath}/events/${eventId}/preview`}
          className="evSetBtn isSmall evSetLinkBtn"
          onClick={(e) => {
            // 移動するとイベント設定の画面は閉じるので、未保存の変更は破棄される
            if (dirty && !window.confirm("イベント設定の未保存の変更は、破棄されます。プレビューを開きますか？")) {
              e.preventDefault();
            }
          }}
        >
          プレビューを表示
        </Link>
      </div>

      <PdfList eventId={eventId} kind="front" label="表用" items={data.front} />
      <PdfList eventId={eventId} kind="back" label="裏用" items={data.back} />
    </>
  );
}
